/**
 * copilotPhaseConfig.ts — Data-driven phase & subphase configuration
 *
 * Defines the workflow structure that the CopilotTray renders.
 * To change the workflow flow, modify this config — not the component.
 *
 * Design principles:
 *   - Phases are ordered but the tray reads order from this array
 *   - Subphases define the steps within each phase
 *   - Each subphase declares its type (manual, ai, gate) and optional sessionType
 *   - The tray derives status from workspace + session data, not from hardcoded logic
 *   - All display strings use i18n keys, not hardcoded English
 */

import { WORKSPACE_PHASES, type WorkspacePhase } from "../../api/workspaces";
import type { AiSessionResponse, EvidenceItemResponse } from "../../api/workspaceAi";
import type { TriageEntryResponse } from "../../api/triage";
import type { WorkspaceResponse } from "../../api/workspaces";

// ── Types ──────────────────────────────────────────────────────

export type SubphaseType = "manual" | "ai" | "gate";

export type SubphaseStatus =
  | "completed"
  | "ready"
  | "pending"
  | "locked";

export interface SubphaseConfig {
  /** Unique key within the phase (used for state tracking). */
  key: string;
  /** i18n key under the copilot namespace for the display label. */
  labelKey: string;
  /** What kind of step this is. */
  type: SubphaseType;
  /** If type === "ai", the session_type string sent to the API. */
  sessionType?: string;
  /**
   * Function to determine this subphase's status based on current data.
   * Receives the full workspace context so it can inspect sessions, triage, evidence, etc.
   */
  resolveStatus: (ctx: PhaseContext) => SubphaseStatus;
  /** Optional: i18n key for a hint shown when this step is the next action. */
  hintKey?: string;
}

export interface PhaseConfig {
  /** Phase key matching WORKSPACE_PHASES (e.g. "signal_detection"). */
  key: WorkspacePhase;
  /** 1-based phase number for display. */
  num: number;
  /** i18n key under the pages namespace: workspace.phaseLabels.<key> */
  labelKey: string;
  /** Ordered subphases within this phase. */
  subphases: SubphaseConfig[];
}

/** Context object passed to status resolvers — all the data needed to determine state. */
export interface PhaseContext {
  workspace: WorkspaceResponse;
  sessions: AiSessionResponse[];
  triageEntries: TriageEntryResponse[];
  evidenceItems: EvidenceItemResponse[];
}

/** Computed status for a phase based on workspace.phase and subphase states. */
export type PhaseStatus = "completed" | "in_progress" | "future";

/** Computed phase with runtime status. */
export interface ComputedPhase extends PhaseConfig {
  status: PhaseStatus;
  confidence: number | null;
  completedAt: string | null;
  subphaseStatuses: Array<SubphaseConfig & { status: SubphaseStatus }>;
  completedCount: number;
}

// ── Helper predicates ──────────────────────────────────────────

function hasCompletedSession(ctx: PhaseContext, sessionType: string): boolean {
  return ctx.sessions.some(
    (s) => s.sessionType === sessionType && (s.status === "completed" || s.status === "reviewed")
  );
}

function hasUsableSession(ctx: PhaseContext, sessionType: string): boolean {
  return ctx.sessions.some(
    (s) =>
      s.sessionType === sessionType &&
      (s.status === "completed" || s.status === "reviewed") &&
      !!s.outputPayload
  );
}

function triageSeeded(ctx: PhaseContext): boolean {
  return ctx.triageEntries.length > 0;
}

function round1Complete(ctx: PhaseContext): boolean {
  const r1 = ctx.triageEntries.filter((e) => !!e.triageQuestionGuid);
  return r1.length > 0 && r1.every((e) => !!e.answerText || e.confirmationStatus === "confirmed");
}

function hasRound2(ctx: PhaseContext): boolean {
  return ctx.triageEntries.some((e) => !e.triageQuestionGuid);
}

function triageReadyForAi(ctx: PhaseContext): boolean {
  if (ctx.triageEntries.length === 0) return false;
  const answered = ctx.triageEntries.filter(
    (e) => !!e.answerText || e.confirmationStatus === "confirmed"
  ).length;
  return answered >= Math.ceil(ctx.triageEntries.length / 2);
}

function evidenceAccepted(ctx: PhaseContext): boolean {
  return ctx.evidenceItems.some((e) => e.status !== "recommended");
}

function evidenceCollected(ctx: PhaseContext): boolean {
  return ctx.evidenceItems.some(
    (e) => e.status === "collected" || e.status === "analyzed"
  );
}

// ── Phase Configuration ────────────────────────────────────────

export const PHASE_CONFIG: PhaseConfig[] = [
  {
    key: "signal_detection",
    num: 1,
    labelKey: "workspace.phaseLabels.signal_detection",
    subphases: [
      {
        key: "intake",
        labelKey: "subphases.intake",
        type: "manual",
        hintKey: "subphaseHints.intake",
        resolveStatus: (ctx) => {
          // Intake is done when the workspace has a description or presenting concern
          if (ctx.workspace.description || ctx.workspace.presentingConcern) return "completed";
          return "ready";
        },
      },
      {
        key: "triage_seed",
        labelKey: "subphases.triageSeed",
        type: "manual",
        hintKey: "subphaseHints.triageSeed",
        resolveStatus: (ctx) => {
          if (triageSeeded(ctx)) return "completed";
          // Ready once intake is done
          if (ctx.workspace.description || ctx.workspace.presentingConcern) return "ready";
          return "locked";
        },
      },
      {
        key: "triage_r1",
        labelKey: "subphases.triageR1",
        type: "manual",
        hintKey: "subphaseHints.triageR1",
        resolveStatus: (ctx) => {
          if (round1Complete(ctx)) return "completed";
          if (triageSeeded(ctx)) return "ready";
          return "locked";
        },
      },
      {
        key: "triage_followup",
        labelKey: "subphases.triageFollowup",
        type: "ai",
        sessionType: "triage_followup",
        hintKey: "subphaseHints.triageFollowup",
        resolveStatus: (ctx) => {
          if (hasRound2(ctx)) return "completed";
          if (round1Complete(ctx)) return "ready";
          return "locked";
        },
      },
      {
        key: "triage_r2",
        labelKey: "subphases.triageR2",
        type: "manual",
        hintKey: "subphaseHints.triageR2",
        resolveStatus: (ctx) => {
          if (!hasRound2(ctx)) return "locked";
          if (triageReadyForAi(ctx)) return "completed";
          return "ready";
        },
      },
      {
        key: "signal_processing",
        labelKey: "subphases.generateConcern",
        type: "ai",
        sessionType: "signal_processing",
        hintKey: "subphaseHints.generateConcern",
        resolveStatus: (ctx) => {
          if (hasUsableSession(ctx, "signal_processing")) return "completed";
          if (triageReadyForAi(ctx)) return "ready";
          return "locked";
        },
      },
      {
        key: "validate_concern",
        labelKey: "subphases.validateConcern",
        type: "gate",
        hintKey: "subphaseHints.validateConcern",
        resolveStatus: (ctx) => {
          if (ctx.workspace.presentingConcernValidatedAt) return "completed";
          if (hasUsableSession(ctx, "signal_processing")) return "ready";
          return "locked";
        },
      },
    ],
  },
  {
    key: "detection_validation",
    num: 2,
    labelKey: "workspace.phaseLabels.detection_validation",
    subphases: [
      {
        key: "evidence_plan",
        labelKey: "subphases.evidencePlan",
        type: "ai",
        sessionType: "evidence_plan",
        hintKey: "subphaseHints.evidencePlan",
        resolveStatus: (ctx) => {
          if (hasCompletedSession(ctx, "evidence_plan")) return "completed";
          return "ready";
        },
      },
      {
        key: "collect_immediate",
        labelKey: "subphases.collectImmediate",
        type: "manual",
        hintKey: "subphaseHints.collectImmediate",
        resolveStatus: (ctx) => {
          if (evidenceAccepted(ctx)) return "completed";
          if (hasCompletedSession(ctx, "evidence_plan")) return "ready";
          return "pending";
        },
      },
      {
        key: "collect_shortterm",
        labelKey: "subphases.collectShortterm",
        type: "manual",
        hintKey: "subphaseHints.collectShortterm",
        resolveStatus: (ctx) => {
          if (evidenceCollected(ctx)) return "completed";
          if (evidenceAccepted(ctx)) return "ready";
          return "pending";
        },
      },
      {
        key: "initial_analysis",
        labelKey: "subphases.initialAnalysis",
        type: "ai",
        sessionType: "initial_analysis",
        hintKey: "subphaseHints.initialAnalysis",
        resolveStatus: (ctx) => {
          if (hasCompletedSession(ctx, "initial_analysis")) return "completed";
          if (evidenceAccepted(ctx)) return "ready";
          return "locked";
        },
      },
      {
        key: "compile_findings",
        labelKey: "subphases.compileFindings",
        type: "gate",
        hintKey: "subphaseHints.compileFindings",
        resolveStatus: (ctx) => {
          if (hasCompletedSession(ctx, "initial_analysis") && evidenceCollected(ctx)) return "ready";
          if (hasCompletedSession(ctx, "initial_analysis")) return "pending";
          return "locked";
        },
      },
    ],
  },
  {
    key: "analysis",
    num: 3,
    labelKey: "workspace.phaseLabels.analysis",
    subphases: [
      {
        key: "causal_analysis",
        labelKey: "subphases.causalAnalysis",
        type: "ai",
        sessionType: "causal_analysis",
        hintKey: "subphaseHints.causalAnalysis",
        resolveStatus: (ctx) => {
          if (hasCompletedSession(ctx, "causal_analysis")) return "completed";
          return "ready";
        },
      },
      {
        key: "evidence_synthesis",
        labelKey: "subphases.evidenceSynthesis",
        type: "ai",
        sessionType: "evidence_synthesis",
        hintKey: "subphaseHints.evidenceSynthesis",
        resolveStatus: (ctx) => {
          if (hasCompletedSession(ctx, "evidence_synthesis")) return "completed";
          if (hasCompletedSession(ctx, "causal_analysis")) return "ready";
          return "locked";
        },
      },
    ],
  },
  {
    key: "solution_design",
    num: 4,
    labelKey: "workspace.phaseLabels.solution_design",
    subphases: [
      {
        key: "solution_generation",
        labelKey: "subphases.solutionGeneration",
        type: "ai",
        sessionType: "solution_generation",
        hintKey: "subphaseHints.solutionGeneration",
        resolveStatus: (ctx) => {
          if (hasCompletedSession(ctx, "solution_generation")) return "completed";
          return "ready";
        },
      },
    ],
  },
  {
    key: "resourcing",
    num: 5,
    labelKey: "workspace.phaseLabels.resourcing",
    subphases: [
      {
        key: "resource_plan",
        labelKey: "subphases.resourcePlan",
        type: "ai",
        sessionType: "resource_plan",
        hintKey: "subphaseHints.resourcePlan",
        resolveStatus: (ctx) => {
          if (hasCompletedSession(ctx, "resource_plan")) return "completed";
          return "ready";
        },
      },
    ],
  },
  {
    key: "implementation",
    num: 6,
    labelKey: "workspace.phaseLabels.implementation",
    subphases: [
      {
        key: "implementation_tracking",
        labelKey: "subphases.implementationTracking",
        type: "ai",
        sessionType: "implementation_tracking",
        hintKey: "subphaseHints.implementationTracking",
        resolveStatus: (ctx) => {
          if (hasCompletedSession(ctx, "implementation_tracking")) return "completed";
          return "ready";
        },
      },
    ],
  },
  {
    key: "sustainment",
    num: 7,
    labelKey: "workspace.phaseLabels.sustainment",
    subphases: [
      {
        key: "impact_assessment",
        labelKey: "subphases.impactAssessment",
        type: "ai",
        sessionType: "impact_assessment",
        hintKey: "subphaseHints.impactAssessment",
        resolveStatus: (ctx) => {
          if (hasCompletedSession(ctx, "impact_assessment")) return "completed";
          return "ready";
        },
      },
      {
        key: "monitoring_plan",
        labelKey: "subphases.monitoringPlan",
        type: "ai",
        sessionType: "monitoring_plan",
        hintKey: "subphaseHints.monitoringPlan",
        resolveStatus: (ctx) => {
          if (hasCompletedSession(ctx, "monitoring_plan")) return "completed";
          if (hasCompletedSession(ctx, "impact_assessment")) return "ready";
          return "locked";
        },
      },
    ],
  },
];

// ── Runtime computation ────────────────────────────────────────

/**
 * Compute the full phase tree with runtime statuses.
 * This is the single source of truth for the tray's display state.
 */
export function computePhases(ctx: PhaseContext): ComputedPhase[] {
  const currentPhaseIndex = WORKSPACE_PHASES.indexOf(
    ctx.workspace.phase as WorkspacePhase
  );

  return PHASE_CONFIG.map((phase, idx) => {
    // Determine phase-level status
    let status: PhaseStatus;
    if (idx < currentPhaseIndex) {
      status = "completed";
    } else if (idx === currentPhaseIndex) {
      status = "in_progress";
    } else {
      status = "future";
    }

    // Compute subphase statuses
    const subphaseStatuses = phase.subphases.map((sub) => ({
      ...sub,
      status: status === "future" ? ("locked" as SubphaseStatus) : sub.resolveStatus(ctx),
    }));

    const completedCount = subphaseStatuses.filter((s) => s.status === "completed").length;

    // Find the best confidence score from completed sessions in this phase
    const phaseSessions = ctx.sessions.filter(
      (s) =>
        s.phase === phase.key &&
        (s.status === "completed" || s.status === "reviewed") &&
        s.confidenceScore != null
    );
    const confidence =
      phaseSessions.length > 0
        ? phaseSessions.reduce((best, s) => Math.max(best, s.confidenceScore ?? 0), 0)
        : null;

    // Find completion date (last session completion or phase transition)
    const completedAt =
      status === "completed" && phaseSessions.length > 0
        ? phaseSessions
            .map((s) => s.createdAt)
            .sort()
            .pop() ?? null
        : null;

    return {
      ...phase,
      status,
      confidence,
      completedAt,
      subphaseStatuses,
      completedCount,
    };
  });
}

/**
 * Find the first actionable subphase in the current phase.
 * Returns the subphase config + its computed status, or null if nothing is ready.
 */
export function findNextStep(
  phases: ComputedPhase[]
): (SubphaseConfig & { status: SubphaseStatus; phaseNum: number; phaseKey: string; stepIndex: number; totalSteps: number }) | null {
  const currentPhase = phases.find((p) => p.status === "in_progress");
  if (!currentPhase) return null;

  // Find the first subphase that is "ready"
  const readyIdx = currentPhase.subphaseStatuses.findIndex((s) => s.status === "ready");
  if (readyIdx >= 0) {
    const sub = currentPhase.subphaseStatuses[readyIdx];
    return {
      ...sub,
      phaseNum: currentPhase.num,
      phaseKey: currentPhase.key,
      stepIndex: readyIdx,
      totalSteps: currentPhase.subphases.length,
    };
  }

  return null;
}
